import { useMemo, useState } from 'react';

import { renderMarkdown, toggleTaskItem } from '@/lib/markdown';
import { cn } from '@/lib/utils';
import { ImageLightbox } from '@/components/ui/lightbox';

/**
 * Rendered Markdown.
 *
 * `dangerouslySetInnerHTML` is safe *here specifically* because
 * `renderMarkdown` never emits raw HTML from its input — see the reasoning in
 * `lib/markdown.ts`. Do not point this component at HTML from anywhere else.
 */
export function Markdown({
  source,
  className,
  onToggleTask,
}: {
  source: string;
  className?: string;
  /**
   * Called with the rewritten Markdown when a `- [ ]` is ticked.
   *
   * Omit it and the checkboxes render disabled — a checklist you can click in a
   * preview but that saves nothing is worse than one that plainly cannot be
   * clicked.
   */
  onToggleTask?: (next: string) => void;
}) {
  const html = useMemo(
    () => renderMarkdown(source, { interactive: Boolean(onToggleTask) }),
    [source, onToggleTask],
  );
  const [zoomed, setZoomed] = useState<{ src: string; alt: string } | null>(null);

  return (
    <>
      <div
        className={cn('markdown text-sm text-content-muted', className)}
        dangerouslySetInnerHTML={{ __html: html }}
        // Delegated rather than bound per image: the HTML is injected, so there
        // are no React elements to attach handlers to.
        onClick={(event) => {
          const target = event.target;

          // A task checkbox rewrites the *Markdown* — the description is the
          // stored value, so the tick has to change the text, not the DOM.
          if (onToggleTask && target instanceof Element) {
            // The box itself, or anywhere on its item: on a phone the text is
            // the only target big enough to hit reliably. Links inside the
            // item still navigate.
            const box =
              target instanceof HTMLInputElement && target.classList.contains('md-task')
                ? target
                : target.closest('a')
                  ? null
                  : target.closest('li')?.querySelector<HTMLInputElement>(':scope > input.md-task, :scope > p > input.md-task') ?? null;
            if (box && !box.disabled) {
              event.preventDefault();
              event.stopPropagation();
              const index = Number(box.dataset.taskIndex ?? '-1');
              if (index >= 0) onToggleTask(toggleTaskItem(source, index));
              return;
            }
          }

          if (!(target instanceof HTMLImageElement)) return;
          event.preventDefault();
          event.stopPropagation();
          setZoomed({ src: target.src, alt: target.alt });
        }}
        role="presentation"
      />
      {zoomed ? (
        <ImageLightbox
          src={zoomed.src}
          alt={zoomed.alt}
          onClose={() => setZoomed(null)}
        />
      ) : null}
    </>
  );
}
